import instance from "../utils/network";

export interface SellerPubMissionMemberStatsRequest {
  pubId: number;
  page: number;
  limit: number;
  nickname?: string;
}

export interface SellerMemberMissionListRequest {
  pubId?: number;
  memberId: number;
  page: number;
  limit: number;
  name?: string;
  startDate?: string;
  endDate?: string;
}

export interface SellerMemberBingoListRequest extends SellerMemberMissionListRequest {}

export interface SellerMemberMissionDetailRequest {
  memberId: number;
  pubId?: number;
  memberProgressId?: number;
}

export interface SellerMemberBingoDetailRequest {
  memberId: number;
  pubId?: number;
}

export interface SellerMemberMissionActionRequest {
  memberId: number;
  pubId?: number;
  memberProgressId?: number;
  memo?: string;
}

export interface SellerMemberMissionProgressRequest {
  memberId: number;
  pubId?: number;
  memberProgressId?: number;
  currentCount: number;
}

export interface SellerMemberBingoCellActionRequest {
  memberId: number;
  pubId?: number;
  memo?: string;
}

export interface SellerMemberBingoCellProgressRequest {
  memberId: number;
  pubId?: number;
  currentCount: number;
}

export interface SellerPubMissionMemberStatsItemResponse {
  memberId: number;
  nickname: string;
  missionCompleteCount: number;
  bingoCompleteCount: number;
  unclaimedCount: number;
  latestProcessedAt: string | null;
}

export interface SellerPubMissionMemberStatsListResponse {
  total: number;
  list: SellerPubMissionMemberStatsItemResponse[];
}

export interface SellerMemberMissionItemResponse {
  id: number;
  missionId: number;
  memberProgressId: number | null;
  name: string;
  progressLabel: string;
}

export interface SellerMemberMissionListResponse {
  total: number;
  list: SellerMemberMissionItemResponse[];
}

export interface SellerMemberBingoItemResponse {
  id: number;
  name: string;
  progressLabel: string;
}

export interface SellerMemberBingoListResponse {
  total: number;
  list: SellerMemberBingoItemResponse[];
}

export interface SellerMemberMissionRewardItemResponse {
  id: number;
  name: string;
  type: string;
  typeLabel: string;
  count?: number;
  stageLabel: string;
  isPaid: boolean;
  payoutStatusLabel: string;
}

export interface SellerMemberMissionDetailResponse {
  id: number;
  name: string;
  type: string;
  typeLabel: string;
  isComplete: boolean;
  progressStatusLabel: string;
  currentCount: number;
  targetCount: number | null;
  progressLabel: string;
  canComplete: boolean;
  rewards: SellerMemberMissionRewardItemResponse[];
}

export interface SellerMemberBingoRewardItemResponse {
  id: number;
  stageId: number;
  name: string;
  type: string;
  typeLabel: string;
  count?: number;
  stageRow: number;
  stageLabel: string;
  isAchieved: boolean;
  isPaid: boolean;
  payoutStatusLabel: string;
}

export interface SellerMemberBingoCellItemResponse {
  id: number;
  cellNumber: number;
  row: number;
  column: number;
  name: string;
  type: string;
  typeLabel: string;
  currentCount: number;
  targetCount: number | null;
  isComplete: boolean;
  progressStatusLabel: string;
  progressLabel: string;
  canComplete: boolean;
  canCancelComplete: boolean;
}

export interface SellerMemberBingoDetailResponse {
  id: number;
  name: string;
  column: number;
  sizeLabel: string;
  isComplete: boolean;
  progressStatusLabel: string;
  completedLines: number;
  maxLines: number;
  completedCellCount: number;
  totalCellCount: number;
  progressLabel: string;
  canComplete: boolean;
  cells: SellerMemberBingoCellItemResponse[];
  rewards: SellerMemberBingoRewardItemResponse[];
}

export function getPubMissionMemberStatsList(
  request: SellerPubMissionMemberStatsRequest,
): Promise<SellerPubMissionMemberStatsListResponse> {
  return instance.get("/seller/pub-mission-member", { params: request });
}

export function getInProgressMissions(
  request: SellerMemberMissionListRequest,
): Promise<SellerMemberMissionListResponse> {
  return instance.get("/seller/pub-mission-member/missions/in-progress", {
    params: request,
  });
}

export function getCompletedMissions(
  request: SellerMemberMissionListRequest,
): Promise<SellerMemberMissionListResponse> {
  return instance.get("/seller/pub-mission-member/missions/completed", {
    params: request,
  });
}

export function getMemberMissionDetail(
  missionId: number,
  request: SellerMemberMissionDetailRequest,
): Promise<SellerMemberMissionDetailResponse> {
  return instance.get(`/seller/pub-mission-member/missions/${missionId}`, {
    params: request,
  });
}

export function completeMemberMission(
  missionId: number,
  request: SellerMemberMissionActionRequest,
): Promise<{ code: number; message: string }> {
  return instance.put(
    `/seller/pub-mission-member/missions/${missionId}/complete`,
    request,
  );
}

export function cancelMemberMissionComplete(
  missionId: number,
  request: SellerMemberMissionActionRequest,
): Promise<{ code: number; message: string }> {
  return instance.put(
    `/seller/pub-mission-member/missions/${missionId}/cancel-complete`,
    request,
  );
}

export function updateMemberMissionProgress(
  missionId: number,
  request: SellerMemberMissionProgressRequest,
): Promise<{ code: number; message: string }> {
  return instance.put(
    `/seller/pub-mission-member/missions/${missionId}/progress`,
    request,
  );
}

export function getInProgressBingos(
  request: SellerMemberBingoListRequest,
): Promise<SellerMemberBingoListResponse> {
  return instance.get("/seller/pub-mission-member/bingos/in-progress", {
    params: request,
  });
}

export function getCompletedBingos(
  request: SellerMemberBingoListRequest,
): Promise<SellerMemberBingoListResponse> {
  return instance.get("/seller/pub-mission-member/bingos/completed", {
    params: request,
  });
}

export function getMemberBingoDetail(
  bingoId: number,
  request: SellerMemberBingoDetailRequest,
): Promise<SellerMemberBingoDetailResponse> {
  return instance.get(`/seller/pub-mission-member/bingos/${bingoId}`, {
    params: request,
  });
}

export function completeMemberBingoCell(
  bingoId: number,
  cellId: number,
  request: SellerMemberBingoCellActionRequest,
): Promise<{ code: number; message: string }> {
  return instance.put(
    `/seller/pub-mission-member/bingos/${bingoId}/cells/${cellId}/complete`,
    request,
  );
}

export function cancelMemberBingoCellComplete(
  bingoId: number,
  cellId: number,
  request: SellerMemberBingoCellActionRequest,
): Promise<{ code: number; message: string }> {
  return instance.put(
    `/seller/pub-mission-member/bingos/${bingoId}/cells/${cellId}/cancel-complete`,
    request,
  );
}

export function updateMemberBingoCellProgress(
  bingoId: number,
  cellId: number,
  request: SellerMemberBingoCellProgressRequest,
): Promise<{ code: number; message: string }> {
  return instance.put(
    `/seller/pub-mission-member/bingos/${bingoId}/cells/${cellId}/progress`,
    request,
  );
}
